import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { X, XCircle } from 'lucide-react';

export default function CancelInterviewModal({ isOpen, onClose, onConfirm, candidateName }) {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [selectedReason, setSelectedReason] = useState('');
  const [isShaking, setIsShaking] = useState(false);
  const [otherComment, setOtherComment] = useState('');
  const textareaRef = useRef(null);

  const cancellationReasons = [
    'Have another job offer',
    'No longer interested in this role',
    'Process feels too long or complex',
    'Salary / role expectations don\'t match',
    'Scheduling conflict',
    'Technical issues',
    'Applied by mistake / wrong role',
    'Personal reasons',
    'Other'
  ];


  // Reset it in the useEffect
  useEffect(() => {
    if (!isOpen) {
        setSelectedReason('');
        setOtherComment('');
        setIsShaking(false);
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedReason('');
      setIsShaking(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedReason) {
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [selectedReason]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (selectedReason) {
        onConfirm?.(selectedReason, otherComment.trim());
        onClose();
    }
  };

  const handleBackdropClick = () => {
    if (selectedReason) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[99999999]">
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15%       { transform: translateX(-6px); }
          30%       { transform: translateX(6px); }
          45%       { transform: translateX(-5px); }
          60%       { transform: translateX(5px); }
          75%       { transform: translateX(-3px); }
          90%       { transform: translateX(3px); }
        }
        .shake { animation: shake 0.5s ease; }
      `}</style>
      
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-bgPrimary/50 dark:bg-black/20 bg-opacity-50"
        style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
        onMouseDown={handleBackdropClick}
      />

      {/* Modal */}
      <div
        className={`w-full max-w-lg p-6 rounded-xl shadow-lg relative bg-bgSecondary max-h-[80vh] overflow-y-auto ${
          isShaking ? 'shake' : ''
        }`}
        style={{
          background: isDarkMode
            ? 'linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))'
            : '',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(50px)',
        }}
      >
        {/* Header with Icon */}
        <div className="flex flex-col mb-6">
          <h2 className="text-textPrimary dark:text-[#C1A0D0] text-xl font-semibold mb-1">
            Cancel Interview
          </h2>
          <p className="text-textSecondary dark:text-gray-400 text-sm">
            We're sorry to see you go. Help us understand why.
          </p>
          <button
            onClick={onClose}
            className="absolute top-6 right-6"
          >
            <X className="h-5 w-5 text-textPrimary dark:text-[#C1A0D0]" />
          </button>
        </div>

        {/* Question */}
        <div className="mb-4">
          {/* Reason Options */}
            <div className="grid grid-cols-2 gap-2.5">
              {cancellationReasons.map((reason, index) => (
                  <button
                  key={index}
                  onClick={() => setSelectedReason(reason)}
                  className={`w-full px-4 py-3 rounded-lg text-left text-sm transition-all duration-200 ${
                      selectedReason === reason
                      ? 'selected-reason-btn bg-purple-200/30 text-textPrimary dark:text-[#C1A0D0] dark:bg-purple-900/30'
                      : 'border border-gray-400 dark:border-gray-500 bg-bgPrimary dark:bg-bgSecondary text-textSecondary dark:text-gray-300 hover:border-purple-300 dark:hover:border-purple-500'
                  }`}
                  >
                  <div className="flex items-center gap-3">
                      <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          selectedReason === reason
                          ? 'border-transparent bg-gradient-to-r from-pink-500 to-blue-500'
                          : 'border-gray-400 dark:border-gray-300'
                      }`}
                      >
                      {selectedReason === reason && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                      </div>
                      <span className={selectedReason === reason ? 'font-medium' : ''}>
                      {reason}
                      </span>
                  </div>
                  </button>
              ))}
            </div>

            {/* Other comment input */}
            {selectedReason && (
            <textarea
                ref={textareaRef}
                value={otherComment}
                onChange={(e) => setOtherComment(e.target.value)}
                placeholder="Please describe your reason..."
                rows={3}
                className="mt-3 w-full px-4 py-3 rounded-lg border border-purple-400  text-textPrimary dark:text-[#C1A0D0] text-sm placeholder:text-textTertiary focus:outline-none resize-none transition-all"
            />
            )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6 justify-end">
          <button
            onClick={onClose}
            className=" px-6 py-2.5 rounded-full text-sm font-medium border border-gray-400 dark:border-gray-500 text-textPrimary dark:text-gray-400 bg-bgPrimary dark:bg-bgSecondary hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            Keep Interview
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedReason}
            className={`px-6 py-2 rounded-full text-sm ${
              selectedReason
                ? 'text-white bg-gradient-to-r from-pink-500 to-blue-500'
                : 'text-white/50 bg-gray-300 dark:bg-gray-600 disabled:bg-none dark:disabled:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-100'
            }`}
          >
            Confirm Cancellation
          </button>
        </div>

        {/* Footer */}
        {/* {candidateName && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-textTertiary text-center">
              Cancelling interview for <span className="font-medium">{candidateName}</span>
            </p>
          </div>
        )} */}
      </div>
    </div>
  );
}
